/**
 * useInAppAlertToasts — pop-up (toast) DENTRO do ConsoleAI quando chega um alerta.
 *
 * Espelha o push do desktop, mas dentro do app. Implementado por POLLING do feed
 * autenticado (não por SSE): o EventSource não consegue enviar o header
 * Authorization: Bearer, então o stream SSE dá 401 e nunca conecta — por isso o
 * toast usa o mesmo caminho do sino (GET /v1/me/notifications/feed, via api client
 * com Bearer), que é confiável. Latência ~POLL_MS (o push do desktop cobre o
 * instantâneo).
 *
 * Regras de match (canal 'inapp' por source, na tela de Preferências):
 *  - Preferência 'inapp' explícita para a source → respeita (enabled + min_severity).
 *  - Sem preferência → default-on só 'zabbix' em severidade >= critical.
 *
 * Comportamento do toast (decisão do produto): some sozinho após ~5s,
 * deslizando para fora (autoClose=TOAST_AUTOCLOSE_MS); ao clicar, navega para
 * /alertas (Zabbix) ou /notificacoes (e o sino preserva o histórico).
 *
 * Baseline: no 1º carregamento marca os não-lidos atuais como "vistos" (não
 * toasta backlog) — só alertas que chegam DEPOIS do mount viram pop-up.
 *
 * Montado uma vez no AppLayout (shell autenticado).
 */
import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { Box, Text } from '@mantine/core'
import { notificationsFeedApi, type FeedItem } from '@/api/notifications-feed'
import {
  notificationPreferencesApi,
  type Subscription,
} from '@/api/notification-preferences'

const POLL_MS = 20_000
/** Tempo que o toast in-app fica na tela antes de sumir deslizando. */
const TOAST_AUTOCLOSE_MS = 5_000

const RANK: Record<string, number> = {
  debug: -1, info: 0, warning: 1, critical: 2, fatal: 3,
}
const SEVERITY_COLOR: Record<string, string> = {
  fatal: 'red', critical: 'red', warning: 'yellow', info: 'blue', debug: 'gray',
}

/** {source -> rank mínimo} a partir das subscriptions 'inapp' habilitadas. */
export function buildInappMap(items: Subscription[]): Record<string, number> {
  const map: Record<string, number> = {}
  for (const s of items) {
    if (s.channel === 'inapp' && s.enabled) {
      map[s.source] = RANK[s.min_severity] ?? 0
    }
  }
  return map
}

/** Decide se um evento deve virar toast in-app, dado o mapa de preferências. */
export function matchesInApp(
  ev: { source?: string; severity?: string },
  inappMap: Record<string, number>,
): boolean {
  if (!ev.source) return false
  const rank = RANK[ev.severity ?? 'info'] ?? 0
  if (Object.prototype.hasOwnProperty.call(inappMap, ev.source)) {
    return rank >= inappMap[ev.source]
  }
  // Default-on: só Zabbix em critical+ (sem preferência explícita).
  return ev.source === 'zabbix' && rank >= RANK.critical
}

function targetUrl(source: string | undefined): string {
  return source === 'zabbix' ? '/alertas' : '/notificacoes'
}

export function useInAppAlertToasts(): void {
  const navigate = useNavigate()
  const navRef = useRef(navigate)
  navRef.current = navigate
  const seenRef = useRef<Set<number>>(new Set())
  const baselineRef = useRef(false)

  // Preferências 'inapp' (canal pop-up) por source.
  const { data: subs } = useQuery({
    queryKey: ['notification-subscriptions', 'inapp-map'],
    queryFn: () => notificationPreferencesApi.listSubscriptions(),
    staleTime: 60_000,
  })
  const inappMap = useMemo(() => buildInappMap(subs?.items ?? []), [subs])
  const inappMapRef = useRef(inappMap)
  inappMapRef.current = inappMap

  // Feed de não-lidos (polling autenticado — mesmo caminho do sino).
  const loggedIn = typeof localStorage !== 'undefined' && !!localStorage.getItem('session_token')
  const { data: feed } = useQuery({
    queryKey: ['inapp-toast-feed'],
    queryFn: () => notificationsFeedApi.list({ status: 'unread', limit: 30 }),
    enabled: loggedIn,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  useEffect(() => {
    if (feed === undefined) return // ainda sem 1ª resposta — não cria baseline vazio
    const items: FeedItem[] = feed.items ?? []

    // 1ª resposta = baseline: marca os não-lidos atuais como vistos (sem toast).
    if (!baselineRef.current) {
      for (const it of items) seenRef.current.add(it.event_id)
      baselineRef.current = true
      return
    }

    for (const it of items) {
      if (seenRef.current.has(it.event_id)) continue
      seenRef.current.add(it.event_id)
      if (!matchesInApp(it, inappMapRef.current)) continue

      const color = SEVERITY_COLOR[it.severity ?? 'info'] ?? 'blue'
      const url = targetUrl(it.source)
      const toastId = `inapp-alert-${it.event_id}`
      notifications.show({
        id: toastId,
        title: it.title || `Alerta (${it.source})`,
        color,
        autoClose: TOAST_AUTOCLOSE_MS,
        withCloseButton: true,
        message: (
          <Box
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => { notifications.hide(toastId); navRef.current(url) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { notifications.hide(toastId); navRef.current(url) } }}
          >
            <Text size="sm">Toque para ver os detalhes</Text>
          </Box>
        ),
      })
    }
  }, [feed])
}
